import {
  type ComponentProps,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Dialog } from 'radix-ui';
import {
  Building2Icon,
  CheckIcon,
  ChevronDownIcon,
  EyeIcon,
  LockIcon,
  SearchIcon,
  UsersIcon,
  XIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from '@/components/ui/menu';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { formatCount } from '@/i18n/format';
import copyCatalog from '@/i18n/ns/sharing-sheet';
import { sharingApi } from '../sharing-api';

// The one Share sheet (operator design 2026-10-09). Every item that can be
// shared opens this frame from the same Share button: who can open it, and,
// read-only, what it runs on. Each surface keeps its own writer and checks;
// these parts only draw and collect the choice.

export type SheetAudience = 'private' | 'company' | 'people';
export type SheetRole = 'viewer' | 'editor';
export type SheetPerson = { userId: string; displayName: string | null; email: string };
export type SheetMember = { userId: string; role: SheetRole };
export type SheetRoleOption = { role: SheetRole; label: string; body: string };
type Visibility = 'private' | 'company' | 'members';

export const useSheetCopy = () => useNamespaceCopy(copyCatalog);
export type SheetCopy = ReturnType<typeof useSheetCopy>;

export const toSheetAudience = (visibility: Visibility | undefined): SheetAudience =>
  visibility === 'members' ? 'people' : (visibility ?? 'private');
export const toVisibility = (audience: SheetAudience): Visibility =>
  audience === 'people' ? 'members' : audience;

/** The status line under a sheet: who can open it now. */
export function audienceStatus(
  copy: SheetCopy,
  locale: string,
  audience: SheetAudience,
  people: number,
  company: string,
) {
  if (audience === 'private') return copy.statusPrivate;
  if (audience === 'company') return formatCopy(copy.statusCompany, { company });
  if (!people) return copy.statusPeopleEmpty;
  return formatCopy(copy.statusPeople, {
    people: formatCount(copy.peopleCount, people, locale),
  });
}

/** Can use / Can edit, or Can view for a chat (only its owner writes). */
export function roleOptions(copy: SheetCopy, kind: 'work' | 'file' | 'chat'): SheetRoleOption[] {
  if (kind === 'chat') return [{ role: 'viewer', label: copy.canView, body: copy.canViewBody }];
  if (kind === 'file')
    return [
      { role: 'viewer', label: copy.canView, body: copy.canViewBody },
      { role: 'editor', label: copy.canEdit, body: copy.canEditFile },
    ];
  return [
    { role: 'viewer', label: copy.canUse, body: copy.canUseBody },
    { role: 'editor', label: copy.canEdit, body: copy.canEditBody },
  ];
}

/** The header Share button. Its glyph says who can open the item now. */
export function ShareTrigger({
  visibility,
  viewer = false,
  audienceLabel,
  label,
  testId,
  disabled,
  onClick,
  ref,
  ...rest
}: {
  visibility?: Visibility;
  viewer?: boolean;
  audienceLabel?: string;
  /** Words of its own instead of Share, as an invitation to bring people in. */
  label?: string;
  testId?: string;
  disabled?: boolean;
  onClick?: () => void;
  ref?: Ref<HTMLButtonElement>;
}) {
  const copy = useSheetCopy();
  const Icon =
    label || viewer || visibility === 'members'
      ? UsersIcon
      : visibility === 'company'
        ? Building2Icon
        : LockIcon;
  return (
    <Button
      {...rest}
      ref={ref}
      type="button"
      variant="outline"
      size="sm"
      // ws-canvas-audience: the canvas header sizes it like its neighbouring controls.
      className="ws-share-trigger ws-canvas-audience"
      data-testid={testId}
      data-visibility={viewer ? 'shared' : (visibility ?? 'private')}
      // A trigger with its own words is named by them; who it is shared with stays in the title.
      aria-label={
        audienceLabel && !label
          ? viewer
            ? `${copy.shared}: ${audienceLabel}`
            : formatCopy(copy.shareWith, { audience: audienceLabel })
          : undefined
      }
      title={audienceLabel}
      disabled={disabled}
      onClick={onClick}
    >
      <Icon aria-hidden="true" />
      {label ?? (viewer ? copy.shared : copy.share)}
    </Button>
  );
}

/** The sheet itself. Mount it inside an open Dialog.Root. */
export function ShareSheet({
  eyebrow,
  title,
  icon,
  status,
  actions,
  busy = false,
  testId,
  children,
}: {
  eyebrow: string;
  title: string;
  icon?: ReactNode;
  status?: ReactNode;
  actions: ReactNode;
  busy?: boolean;
  testId?: string;
  children: ReactNode;
}) {
  const copy = useSheetCopy();
  const hold = (event: Event) => {
    if (busy) event.preventDefault();
  };
  return (
    <Dialog.Portal>
      <Dialog.Overlay className="ws-sharesheet-scrim" />
      <Dialog.Content
        className="ws-sharesheet"
        data-testid={testId}
        aria-describedby={undefined}
        onEscapeKeyDown={(event) => {
          hold(event);
          // Esc in a search with text clears the search; the sheet stays open.
          const target = event.target;
          if (target instanceof HTMLInputElement && target.type === 'search' && target.value)
            event.preventDefault();
        }}
        onInteractOutside={hold}
      >
        <header className="ws-sharesheet-head">
          <span className="ws-sharesheet-icon" aria-hidden="true">
            {icon ?? <UsersIcon />}
          </span>
          <div className="ws-sharesheet-heading">
            <span className="ws-sharesheet-eyebrow">{eyebrow}</span>
            <Dialog.Title className="ws-sharesheet-title">{title}</Dialog.Title>
          </div>
          <Dialog.Close className="ws-sharesheet-close" aria-label={copy.close} disabled={busy}>
            <XIcon aria-hidden="true" />
          </Dialog.Close>
        </header>
        <div className="ws-sharesheet-body">{children}</div>
        <footer className="ws-sharesheet-foot">
          <span className="ws-sharesheet-status" role="status">
            {status}
          </span>
          <div className="ws-sharesheet-actions">{actions}</div>
        </footer>
      </Dialog.Content>
    </Dialog.Portal>
  );
}

export function SheetSection({
  title,
  help,
  testId,
  children,
}: {
  title: string;
  help?: ReactNode;
  testId?: string;
  children?: ReactNode;
}) {
  return (
    <section className="ws-sharesheet-section" data-testid={testId}>
      <div className="ws-sharesheet-section-head">
        <h3>{title}</h3>
        {help && <p className="ws-sharesheet-help">{help}</p>}
      </div>
      {children}
    </section>
  );
}

/** A calm note in the sheet: a consequence to know before saving, never a choice. */
export function SheetNote({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <p className="ws-sharesheet-note" role="note" data-testid={testId}>
      <EyeIcon aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
}

export function SheetButton({
  primary = false,
  small = false,
  className = '',
  ...props
}: ComponentProps<'button'> & { primary?: boolean; small?: boolean }) {
  return (
    <button
      type="button"
      {...props}
      className={`ws-sharesheet-button${primary ? ' primary' : ''}${small ? ' small' : ''} ${className}`.trim()}
    />
  );
}

/** Cancel and Save while something changed; Done otherwise. */
export function SheetFooter({
  dirty,
  busy = false,
  disabled = false,
  saveLabel,
  saveTestId,
  onSave,
}: {
  dirty: boolean;
  busy?: boolean;
  /** Save is unavailable for a reason other than saving. */
  disabled?: boolean;
  saveLabel?: string;
  saveTestId?: string;
  onSave: () => void;
}) {
  const copy = useSheetCopy();
  if (!dirty)
    return (
      <Dialog.Close asChild>
        <SheetButton primary>{copy.done}</SheetButton>
      </Dialog.Close>
    );
  return (
    <>
      <Dialog.Close asChild>
        <SheetButton disabled={busy}>{copy.cancel}</SheetButton>
      </Dialog.Close>
      <SheetButton primary data-testid={saveTestId} disabled={busy || disabled} onClick={onSave}>
        {busy ? copy.saving : (saveLabel ?? copy.save)}
      </SheetButton>
    </>
  );
}

/** Whether two audiences name the same people with the same roles, in any order. */
export function sameAudience(a: readonly SheetMember[], b: readonly SheetMember[]) {
  const key = (list: readonly SheetMember[]) =>
    list
      .map(({ userId, role }) => `${userId}\u0000${role}`)
      .sort()
      .join('\u0001');
  return key(a) === key(b);
}

export type Colleagues = { people: SheetPerson[]; loading: boolean; failed: boolean };
export const loadColleagues = () => sharingApi.colleagues().then((value) => value.members);

/** The company's colleagues for Specific people: one loader for every Share sheet. */
export function useColleagues(
  load: () => Promise<readonly SheetPerson[]> = loadColleagues,
): Colleagues {
  const [state, setState] = useState<Colleagues>({ people: [], loading: true, failed: false });
  useEffect(() => {
    let active = true;
    load().then(
      (people) =>
        active &&
        setState({
          people: Array.isArray(people) ? [...people] : [],
          loading: false,
          failed: false,
        }),
      () => active && setState({ people: [], loading: false, failed: true }),
    );
    return () => {
      active = false;
    };
  }, [load]);
  return state;
}

/** Only you / Everyone / Specific people: a segmented control, a list on a phone. */
export function AudienceControl({
  value,
  companyName,
  onChange,
  disabled = false,
  reasons = {},
  hide = [],
}: {
  value: SheetAudience;
  companyName: string;
  onChange: (value: SheetAudience) => void;
  disabled?: boolean;
  /** Why a choice is unavailable. The choice stays visible and says so. */
  reasons?: Partial<Record<SheetAudience, string>>;
  hide?: SheetAudience[];
}) {
  const copy = useSheetCopy();
  const choices = (
    [
      ['private', copy.onlyYou, copy.onlyYouBody, LockIcon],
      [
        'company',
        formatCopy(copy.everyone, { company: companyName }),
        copy.everyoneBody,
        Building2Icon,
      ],
      ['people', copy.specificPeople, copy.specificPeopleBody, UsersIcon],
    ] as const
  ).filter(([id]) => !hide.includes(id));
  const index = Math.max(
    0,
    choices.findIndex(([id]) => id === value),
  );
  const style = { '--count': choices.length, '--index': index } as CSSProperties;
  // A radio group: Tab reaches the chosen choice, arrow keys move the choice.
  const open = choices.filter(([id]) => !disabled && !reasons[id]).map(([id]) => id);
  const focusable = open.includes(value) ? value : open[0];
  const step: Partial<Record<string, number>> = {
    ArrowRight: 1,
    ArrowDown: 1,
    ArrowLeft: -1,
    ArrowUp: -1,
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const by = step[event.key];
    if (!by || !open.length) return;
    event.preventDefault();
    const next = open[(open.indexOf(value) + by + open.length) % open.length];
    if (next !== value) onChange(next);
    event.currentTarget.querySelector<HTMLElement>(`[data-value="${next}"]`)?.focus();
  };
  // Why a choice is unavailable, in words on every screen (the phone list
  // also says it under the choice).
  const why = [...new Set(choices.flatMap(([id]) => (reasons[id] ? [reasons[id]] : [])))];
  return (
    <>
      <div
        role="radiogroup"
        aria-label={copy.whoTitle}
        className="ws-segmented"
        style={style}
        data-testid="share-audience"
        onKeyDown={onKeyDown}
      >
        <span className="ws-segmented-thumb" aria-hidden="true" />
        {choices.map(([id, label, body, Icon]) => {
          const reason = reasons[id];
          return (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={value === id}
              className="ws-segment"
              data-value={id}
              disabled={disabled || Boolean(reason)}
              tabIndex={id === focusable ? 0 : -1}
              title={reason ?? label}
              onClick={() => value !== id && onChange(id)}
            >
              <Icon aria-hidden="true" className="ws-segment-icon" />
              <span className="ws-segment-text">
                <span className="ws-segment-label">{label}</span>
                <span className="ws-segment-sub">{reason ?? body}</span>
              </span>
              <CheckIcon aria-hidden="true" className="ws-segment-check" />
            </button>
          );
        })}
      </div>
      {why.length > 0 && (
        <p className="ws-segmented-why" data-testid="share-audience-why">
          {why.join(' ')}
        </p>
      )}
    </>
  );
}

const SERIES = 8;
function avatarStyle(id: string): CSSProperties {
  let hash = 0;
  for (const char of id) hash = (hash * 31 + char.charCodeAt(0)) | 0;
  return { '--avatar': `var(--series-${(Math.abs(hash) % SERIES) + 1})` } as CSSProperties;
}
const initials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toLocaleUpperCase())
    .join('');

export function Avatar({ id, name }: { id: string; name: string }) {
  return (
    <span className="ws-avatar" aria-hidden="true" style={avatarStyle(id)}>
      {initials(name)}
    </span>
  );
}

function RoleMenu({
  name,
  role,
  roles,
  disabled,
  onRole,
  onRemove,
}: {
  name: string;
  role: SheetRole;
  roles: SheetRoleOption[];
  disabled?: boolean;
  onRole: (role: SheetRole) => void;
  onRemove?: () => void;
}) {
  const copy = useSheetCopy();
  const label = formatCopy(copy.accessFor, { name });
  const current = roles.find((option) => option.role === role) ?? roles[0]!;
  return (
    <Menu>
      <MenuTrigger asChild>
        <button type="button" className="ws-role-button" aria-label={label} disabled={disabled}>
          {current.label}
          <ChevronDownIcon aria-hidden="true" />
        </button>
      </MenuTrigger>
      <MenuContent label={label}>
        <MenuRadioGroup value={role} onValueChange={(next) => onRole(next as SheetRole)}>
          {roles.map((option) => (
            <MenuRadioItem key={option.role} value={option.role} description={option.body}>
              {option.label}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
        {onRemove && (
          <>
            <MenuSeparator />
            <MenuItem tone="danger" onSelect={onRemove}>
              {copy.removeAccess}
            </MenuItem>
          </>
        )}
      </MenuContent>
    </Menu>
  );
}

/** One person, or everyone at the company, with what they can do. */
export function PersonRow({
  id,
  name,
  detail,
  company = false,
  fixedLabel,
  role,
  roles,
  disabled,
  onRole,
  onRemove,
}: {
  id: string;
  name: string;
  detail?: string;
  company?: boolean;
  fixedLabel?: string;
  role?: SheetRole;
  roles?: SheetRoleOption[];
  disabled?: boolean;
  onRole?: (role: SheetRole) => void;
  onRemove?: () => void;
}) {
  return (
    <div className="ws-person" data-testid="share-person">
      {company ? (
        <span className="ws-avatar is-company" aria-hidden="true">
          <Building2Icon />
        </span>
      ) : (
        <Avatar id={id} name={name} />
      )}
      <span className="ws-person-text">
        <span className="ws-person-name">{name}</span>
        {detail && <span className="ws-person-detail">{detail}</span>}
      </span>
      {fixedLabel ? (
        <span className="ws-role-fixed">{fixedLabel}</span>
      ) : role && roles && onRole ? (
        <RoleMenu
          name={name}
          role={role}
          roles={roles}
          disabled={disabled}
          onRole={onRole}
          onRemove={onRemove}
        />
      ) : null}
    </div>
  );
}

const personName = (person: SheetPerson | undefined, fallback: string) =>
  person ? person.displayName || person.email : fallback;

/** Specific people: add colleagues by name or email, then pick what each can do. */
export function PeopleList({
  members,
  colleagues,
  roles,
  loading = false,
  failed = false,
  disabled = false,
  exclude = [],
  fixed,
  onChange,
}: {
  members: SheetMember[];
  colleagues: SheetPerson[];
  roles: SheetRoleOption[];
  loading?: boolean;
  failed?: boolean;
  disabled?: boolean;
  /** People who cannot be added (the owner, the person sharing). */
  exclude?: string[];
  /** Rows shown first without a menu, such as the owner. */
  fixed?: ReactNode;
  onChange: (members: SheetMember[]) => void;
}) {
  const copy = useSheetCopy();
  const [query, setQuery] = useState('');
  const byId = useMemo(
    () => new Map(colleagues.map((person) => [person.userId, person])),
    [colleagues],
  );
  const chosen = new Set([...exclude, ...members.map((member) => member.userId)]);
  const needle = query.trim().toLocaleLowerCase();
  const matches = needle
    ? colleagues
        .filter(
          (person) =>
            !chosen.has(person.userId) &&
            `${person.displayName ?? ''} ${person.email}`.toLocaleLowerCase().includes(needle),
        )
        .slice(0, 8)
    : [];
  const add = (userId: string) => {
    setQuery('');
    onChange([...members, { userId, role: roles[0]!.role }]);
  };
  return (
    <div className="ws-people" data-testid="share-people">
      <div className="ws-people-search">
        <label>
          <SearchIcon aria-hidden="true" />
          <input
            type="search"
            aria-label={copy.addPeople}
            placeholder={copy.addPeople}
            value={query}
            disabled={disabled || loading || failed}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape' && query) setQuery('');
            }}
          />
        </label>
        {needle && (
          <div className="ws-people-results">
            {matches.map((person) => (
              <button
                key={person.userId}
                type="button"
                className="ws-people-result"
                onClick={() => add(person.userId)}
              >
                <Avatar id={person.userId} name={personName(person, person.email)} />
                <span className="ws-person-text">
                  <span className="ws-person-name">{personName(person, person.email)}</span>
                  <span className="ws-person-detail">{person.email}</span>
                </span>
              </button>
            ))}
            {!matches.length && <span className="ws-people-empty">{copy.noMatches}</span>}
          </div>
        )}
      </div>
      {loading && <p className="ws-sharesheet-help">{copy.loadingPeople}</p>}
      {failed && (
        <p className="ws-sharesheet-help" role="alert">
          {copy.peopleFailed}
        </p>
      )}
      {!loading && !failed && !colleagues.some((person) => !exclude.includes(person.userId)) && (
        <p className="ws-sharesheet-help">{copy.noColleagues}</p>
      )}
      {fixed}
      {members.map((member) => {
        const person = byId.get(member.userId);
        return (
          <PersonRow
            key={member.userId}
            id={member.userId}
            name={personName(person, '…')}
            detail={person?.displayName ? person.email : undefined}
            role={member.role}
            roles={roles}
            disabled={disabled}
            onRole={(role) =>
              onChange(
                members.map((entry) =>
                  entry.userId === member.userId ? { ...entry, role } : entry,
                ),
              )
            }
            onRemove={() => onChange(members.filter((entry) => entry.userId !== member.userId))}
          />
        );
      })}
    </div>
  );
}
